import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import VeloxCard from '../../components/card/VeloxCard';

const HEX = /^#([0-9a-fA-F]{6})$/;
const MAX_LOGO_BYTES = 200 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

export default function EnterpriseBrandingPage() {
  const { enterpriseId } = useParams();
  const { showSuccess, showError } = useToast();
  const [companyName, setCompanyName] = useState('Your company');
  const [saved, setSaved] = useState({ primaryColour: '#C6A75E', logoDataUrl: '' });
  const [primaryColour, setPrimaryColour] = useState('#C6A75E');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    api
      .get('/api/enterprise/branding', { params: { enterpriseId } })
      .then((res) => {
        if (cancelled) return;
        const branding = res.data.branding || {};
        const colour = HEX.test(branding.primaryColour || '') ? branding.primaryColour : '#C6A75E';
        const logo = branding.logoDataUrl || '';
        setPrimaryColour(colour);
        setLogoDataUrl(logo);
        setSaved({ primaryColour: colour, logoDataUrl: logo });
        if (res.data.companyName) setCompanyName(res.data.companyName);
      })
      .catch((err) => {
        if (!cancelled) showError(err.response?.data?.message || 'Could not load branding');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, showError]);

  const dirty = primaryColour !== saved.primaryColour || logoDataUrl !== saved.logoDataUrl;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const onLogo = (file) => {
    setUploadError('');
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setUploadError('Use a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setUploadError('Keep the logo under 200KB.');
      return;
    }
    setReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUrl(String(reader.result || ''));
      setReading(false);
    };
    reader.onerror = () => {
      setUploadError('Could not read that file.');
      setReading(false);
    };
    reader.readAsDataURL(file);
  };

  const colourValid = HEX.test(primaryColour);

  const save = async (event) => {
    event.preventDefault();
    if (!colourValid) {
      showError('Primary colour must be a six-digit hex value.');
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        '/api/enterprise/branding',
        { primaryColour, logoDataUrl },
        { params: { enterpriseId } }
      );
      setSaved({ primaryColour, logoDataUrl });
      showSuccess('Branding saved. It appears on company cards while the company plan is live.');
    } catch (err) {
      showError(err.response?.data?.message || err.response?.data?.error || 'Could not save branding');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setPrimaryColour(saved.primaryColour);
    setLogoDataUrl(saved.logoDataUrl);
    setUploadError('');
  };

  const previewCard = useMemo(
    () => ({
      name: 'Alex Rivera',
      company: companyName,
      role: 'Partnerships',
      email: 'alex@company.com',
      branding: { primaryColour: colourValid ? primaryColour : '#C6A75E', logoDataUrl },
    }),
    [colourValid, companyName, logoDataUrl, primaryColour]
  );

  if (loading) {
    return (
      <div className="ent-content">
        <EntHero title="Branding" lead="Logo and colour for company cards." />
        <EntSkeleton variant="block" />
      </div>
    );
  }

  return (
    <div className="ent-content">
      <EntHero
        title="Branding"
        lead="These assets appear on company cards while branding access is live. They do not change an individual’s personal Velox profile."
        actions={
          <button type="submit" form="ent-branding-form" className="ent-btn-primary" disabled={saving || !dirty || !colourValid}>
            {saving ? 'Saving…' : 'Save branding'}
          </button>
        }
      />

      {dirty ? (
        <div className="ent-unsaved" role="status">
          <span>You have unsaved branding changes.</span>
          <span className="ent-page-head-actions">
            <button type="button" className="ent-btn-secondary ent-btn-sm" onClick={discard} disabled={saving}>
              Discard
            </button>
            <button type="submit" form="ent-branding-form" className="ent-btn-primary ent-btn-sm" disabled={saving || !colourValid}>
              Save
            </button>
          </span>
        </div>
      ) : null}

      <div className="ent-branding-layout">
        <form id="ent-branding-form" className="ent-brand-studio" onSubmit={save}>
          <section className="ent-panel">
            <h2 className="ent-section-title">Colour</h2>
            <div className="ent-color-row">
              <input
                type="color"
                value={colourValid ? primaryColour : '#C6A75E'}
                onChange={(e) => setPrimaryColour(e.target.value)}
                aria-label="Primary colour"
              />
              <input
                className="ent-input"
                value={primaryColour}
                onChange={(e) => setPrimaryColour(e.target.value)}
                aria-label="Primary colour hex"
                spellCheck="false"
              />
            </div>
            {!colourValid ? <p className="ent-form-error">Use a #RRGGBB hex colour.</p> : null}
            <p className="ent-helper">Used as the accent on company cards. Falls back to Velox gold if cleared later.</p>
          </section>

          <section className="ent-panel">
            <h2 className="ent-section-title">Logo</h2>
            <div className="ent-logo-drop">
              {logoDataUrl ? (
                <img className="ent-logo-preview" src={logoDataUrl} alt="Company logo preview" />
              ) : null}
              <label className="ent-form-group" style={{ marginBottom: 0 }}>
                {logoDataUrl ? 'Replace logo' : 'Upload logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => onLogo(e.target.files?.[0])}
                  disabled={reading}
                />
              </label>
              {reading ? <p className="ent-helper">Reading file…</p> : null}
              {uploadError ? <p className="ent-form-error">{uploadError}</p> : null}
              <ul className="ent-req-list">
                <li>PNG, JPEG or WebP</li>
                <li>Under 200KB</li>
                <li>Square or landscape works best on the card</li>
              </ul>
              {logoDataUrl ? (
                <button type="button" className="ent-btn-secondary ent-btn-sm" onClick={() => setLogoDataUrl('')}>
                  Remove logo
                </button>
              ) : null}
            </div>
          </section>
        </form>

        <section className="ent-panel">
          <h2 className="ent-section-title">Card preview</h2>
          <p className="ent-preview-note">
            Representative preview of a company card. Personal names, titles and contact details still come from each person’s own profile.
          </p>
          <div style={{ marginTop: 16 }}>
            <VeloxCard card={previewCard} variant="presentation" />
          </div>
        </section>
      </div>
    </div>
  );
}
